/**
 * EditorHeader - Header del editor con diseño consistente con el Dashboard
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Typography,
	Button,
	IconButton,
	Avatar,
	Menu,
	MenuItem,
	Divider,
	Tooltip,
	Chip,
	InputBase,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import logoCuadrado from "../../assets/logo-cuadrado.png";

const EditorHeader = ({
	user,
	projectName = "Mi guía",
	isSaved = true,
	isSaving = false,
	onSave,
	onPreview,
	onShare,
	onLogin,
	onLogout,
	onBack,
	onProjectNameChange,
}) => {
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);

	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editName, setEditName] = useState(projectName);
	const inputRef = useRef(null);
	const open = Boolean(anchorEl);

	useEffect(() => {
		setEditName(projectName);
	}, [projectName]);

	useEffect(() => {
		if (isEditingTitle && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditingTitle]);

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};


	return (
		<Box
			component="header"
			sx={{
				position: "sticky",
				top: 0,
				zIndex: 1100,
				backgroundColor: "rgba(255, 255, 255, 0.95)",
				backdropFilter: "saturate(180%) blur(20px)",
				borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
				px: { xs: 1.5, md: 2 },
				py: 1,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
				}}
			>
				{/* Left Section */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					{/* Back Button */}
					<Tooltip title="Volver al dashboard" arrow>
						<IconButton
							onClick={() => navigate("/")}
							sx={{
								width: 40,
								height: 40,
								borderRadius: "12px",
								backgroundColor: "rgba(0, 0, 0, 0.04)",
								"&:hover": {
									backgroundColor: "rgba(99, 102, 241, 0.1)",
									"& svg": {
										color: "#6366F1",
									},
								},
							}}
						>
							<ArrowBackIcon sx={{ fontSize: 20, color: "#64748B" }} />
						</IconButton>
					</Tooltip>

					{/* Logo */}
					<Box
						onClick={() => navigate("/")}
						sx={{
							display: { xs: "none", sm: "flex" },
							alignItems: "center",
							gap: 1,
							cursor: "pointer",
							"&:hover": {
								opacity: 0.8,
							},
						}}
					>
						<Box
							component="img"
							src={logoCuadrado}
							alt="Viajes Molones"
							onClick={() => navigate("/")}
							sx={{
								width: 32,
								height: 32,
								objectFit: "contain",
								cursor: "pointer",
								"&:hover": { opacity: 0.85 },
							}}
						/>
					</Box>

					{/* Divider (línea fina entre logo y título) */}
					<Box
						sx={{
							width: "1px",
							height: 24,
							backgroundColor: "rgba(0, 0, 0, 0.08)",
							display: { xs: "none", md: "block" },
							flexShrink: 0,
						}}
					/>

					{/* Project Name + edit */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.75,
							minHeight: 40,
						}}
					>
						{isEditingTitle ? (
							<Box
								sx={{
									borderBottom: "2px solid #6366F1",
									minWidth: 80,
									maxWidth: { xs: 140, sm: 220, md: 320 },
								}}
							>
								<InputBase
									inputRef={inputRef}
									value={editName}
									onChange={(e) => setEditName(e.target.value)}
									onBlur={() => {
										const name = (editName || "").trim() || projectName;
										onProjectNameChange?.(name);
										setIsEditingTitle(false);
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.target.blur();
										}
										if (e.key === "Escape") {
											setEditName(projectName);
											setIsEditingTitle(false);
											inputRef.current?.blur();
										}
									}}
									sx={{
										fontWeight: 600,
										color: "#1a1a2e",
										fontSize: { xs: "0.875rem", md: "1rem" },
										lineHeight: 1.4,
										"& input": {
											py: 0.5,
											px: 0,
											border: "none",
											outline: "none",
											backgroundColor: "transparent",
										},
									}}
								/>
							</Box>
						) : (
							<>
								<Typography
									sx={{
										fontWeight: 600,
										color: "#1a1a2e",
										fontSize: { xs: "0.875rem", md: "1rem" },
										lineHeight: 1.4,
										maxWidth: { xs: 120, sm: 200, md: 300 },
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{projectName}
								</Typography>
								{onProjectNameChange && (
									<Tooltip title="Cambiar título de la guía" arrow>
										<IconButton
											size="small"
											onClick={() => setIsEditingTitle(true)}
											sx={{
												width: 28,
												height: 28,
												flexShrink: 0,
												display: { xs: "none", md: "flex" },
												alignItems: "center",
												justifyContent: "center",
												color: "#94a3b8",
												"&:hover": {
													backgroundColor: "rgba(99, 102, 241, 0.08)",
													color: "#6366F1",
												},
											}}
										>
											<EditOutlinedIcon sx={{ fontSize: 18 }} />
										</IconButton>
									</Tooltip>
								)}
							</>
						)}
					</Box>

					{/* Save Status */}
					<Chip
						icon={
							isSaving ? (
								<Box
									sx={{
										width: 14,
										height: 14,
										borderRadius: "50%",
										border: "2px solid #6366F1",
										borderTopColor: "transparent",
										animation: "spin 1s linear infinite",
										"@keyframes spin": {
											"0%": { transform: "rotate(0deg)" },
											"100%": { transform: "rotate(360deg)" },
										},
									}}
								/>
							) : isSaved ? (
								<CloudDoneOutlinedIcon sx={{ fontSize: 16 }} />
							) : (
								<CloudOffOutlinedIcon sx={{ fontSize: 16 }} />
							)
						}
						label={
							isSaving ? "Guardando..." : isSaved ? "Guardado" : "Sin guardar"
						}
						size="small"
						sx={{
							display: { xs: "none", sm: "flex" },
							height: 28,
							fontSize: "0.75rem",
							fontWeight: 600,
							backgroundColor: isSaved
								? "rgba(16, 185, 129, 0.1)"
								: "rgba(245, 158, 11, 0.1)",
							color: isSaved ? "#059669" : "#D97706",
							border: "none",
							"& .MuiChip-icon": {
								color: isSaved ? "#10B981" : "#F59E0B",
							},
						}}
					/>
				</Box>

				{/* Right Section */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{/* Save Button - visible en móvil (solo icono) y desktop */}
					{user && (
						<Tooltip title="Guardar (Ctrl+S)" arrow>
							<IconButton
								onClick={onSave}
								sx={{
									width: 40,
									height: 40,
									borderRadius: "12px",
									display: "flex",
									backgroundColor: "rgba(0, 0, 0, 0.04)",
									"&:hover": {
										backgroundColor: "rgba(99, 102, 241, 0.1)",
										"& svg": {
											color: "#6366F1",
										},
									},
								}}
							>
								<SaveOutlinedIcon sx={{ fontSize: 20, color: "#64748B" }} />
							</IconButton>
						</Tooltip>
					)}

					{/* Share Button */}
					<Tooltip title="Compartir" arrow>
						<IconButton
							onClick={onShare}
							sx={{
								width: 40,
								height: 40,
								borderRadius: "12px",
								display: "flex",
								backgroundColor: "rgba(0, 0, 0, 0.04)",
								"&:hover": {
									backgroundColor: "rgba(99, 102, 241, 0.1)",
									"& svg": {
										color: "#6366F1",
									},
								},
							}}
						>
							<ShareOutlinedIcon sx={{ fontSize: 20, color: "#64748B" }} />
						</IconButton>
					</Tooltip>

					{/* Vista previa Button */}
					<Tooltip title="Vista previa" arrow>
						<IconButton
							onClick={onPreview}
							sx={{
								width: 40,
								height: 40,
								borderRadius: "12px",
								display: "flex",
								backgroundColor: "rgba(0, 0, 0, 0.04)",
								"&:hover": {
									backgroundColor: "rgba(99, 102, 241, 0.1)",
									"& svg": {
										color: "#6366F1",
									},
								},
							}}
						>
							<VisibilityOutlinedIcon sx={{ fontSize: 20, color: "#64748B" }} />
						</IconButton>
					</Tooltip>

					{/* User Menu */}
					{!user ? (
						<Button
							variant="text"
							onClick={onLogin}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								color: "#4B5563",
								fontSize: "0.875rem",
								display: { xs: "none", sm: "flex" },
								"&:hover": {
									backgroundColor: "rgba(0, 0, 0, 0.04)",
								},
							}}
						>
							Iniciar sesión
						</Button>
					) : (
						<Box
							onClick={handleMenuClick}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								cursor: "pointer",
								p: 0.5,
								borderRadius: "12px",
								transition: "all 0.15s ease",
								"&:hover": {
									backgroundColor: "rgba(0, 0, 0, 0.04)",
								},
							}}
						>
							<Avatar
								sx={{
									width: 32,
									height: 32,
									background:
										"linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
									fontSize: "0.875rem",
									fontWeight: 600,
								}}
							>
								{user.displayName
									? user.displayName.charAt(0).toUpperCase()
									: user.email?.charAt(0).toUpperCase() || "U"}
							</Avatar>
						</Box>
					)}

					{/* User Dropdown Menu */}
					<Menu
						anchorEl={anchorEl}
						open={open}
						onClose={handleMenuClose}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
						PaperProps={{
							sx: {
								mt: 1,
								minWidth: 220,
								borderRadius: "16px",
								boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
								border: "1px solid rgba(0, 0, 0, 0.06)",
							},
						}}
					>
						<Box sx={{ px: 2, py: 1.5 }}>
							<Typography
								sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.938rem" }}
							>
								{user?.displayName || "Usuario"}
							</Typography>
							<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>
								{user?.email}
							</Typography>
						</Box>
						<Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />
						<MenuItem
							onClick={() => {
								handleMenuClose();
								navigate("/");
							}}
							sx={{ py: 1.25, px: 2, mx: 1, my: 0.5, borderRadius: "8px" }}
						>
							<HomeOutlinedIcon
								sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
							/>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
								Inicio
							</Typography>
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleMenuClose();
								navigate("/profile");
							}}
							sx={{ py: 1.25, px: 2, mx: 1, my: 0.5, borderRadius: "8px" }}
						>
							<PersonOutlineIcon
								sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
							/>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
								Mi perfil
							</Typography>
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleMenuClose();
								navigate("/profile");
							}}
							sx={{ py: 1.25, px: 2, mx: 1, my: 0.5, borderRadius: "8px" }}
						>
							<SettingsOutlinedIcon
								sx={{ mr: 1.5, fontSize: 20, color: "#64748b" }}
							/>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
								Configuración
							</Typography>
						</MenuItem>
						<Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />
						<MenuItem
							onClick={() => {
								handleMenuClose();
								onLogout?.();
							}}
							sx={{
								py: 1.25,
								px: 2,
								mx: 1,
								my: 0.5,
								borderRadius: "8px",
								"&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
							}}
						>
							<LogoutIcon sx={{ mr: 1.5, fontSize: 20, color: "#EF4444" }} />
							<Typography
								sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#EF4444" }}
							>
								Cerrar sesión
							</Typography>
						</MenuItem>
					</Menu>

				</Box>
			</Box>
		</Box>
	);
};

export default EditorHeader;
